/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { FilterState } from '../types/vulnerability';

type AnalysisMode = FilterState['analysisMode'];

type ChartPalette = 'default' | 'colorBlindFriendly' | 'nightMode';

type Density = 'comfortable' | 'compact';

interface UserPreferences {
  defaultAnalysisMode: AnalysisMode;
  tableDensity: Density;
  enableAnimations: boolean;
  preferredPalette: ChartPalette;
  showTrendLines: boolean;
}

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  updatePreferences: (updater: (current: UserPreferences) => UserPreferences) => void;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  defaultAnalysisMode: 'all',
  tableDensity: 'comfortable',
  enableAnimations: true,
  preferredPalette: 'default',
  showTrendLines: true,
};

const UserPreferencesContext = createContext<UserPreferencesContextValue | undefined>(undefined);

export function UserPreferencesProvider({ children }: { children: React.ReactNode }) {
  const [preferences, setPreferences] = useLocalStorage<UserPreferences>('vv:preferences', DEFAULT_PREFERENCES);

  const value = useMemo<UserPreferencesContextValue>(
    () => ({
      preferences,
      updatePreferences: (updater) => {
        setPreferences((prev) => updater(prev));
      },
    }),
    [preferences, setPreferences]
  );

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
}

export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}
